import React, { useState, useEffect } from 'react';

const MathNinjaWarrior = () => {
  const [gameState, setGameState] = useState('menu');
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [enemies, setEnemies] = useState([]);
  const [combo, setCombo] = useState(0);
  const [health, setHealth] = useState(100);
  const [timeLeft, setTimeLeft] = useState(60);

  const generateEnemy = () => {
    const ops = [
      () => {
        const a = Math.floor(Math.random() * 100) + 10;
        const b = Math.floor(Math.random() * 100) + 10;
        return { q: `${a}+${b}`, ans: a + b };
      },
      () => {
        const a = Math.floor(Math.random() * 15) + 5;
        const b = Math.floor(Math.random() * 12) + 2;
        return { q: `${a}×${b}`, ans: a * b };
      },
      () => {
        const a = Math.floor(Math.random() * 200) + 50;
        const b = Math.floor(Math.random() * a);
        return { q: `${a}-${b}`, ans: a - b };
      }
    ];
    
    const gen = ops[Math.floor(Math.random() * ops.length)];
    const { q, ans } = gen();
    
    return {
      question: q,
      answer: ans,
      x: Math.random() * 80 + 10,
      y: Math.random() * 60 + 10,
      id: Date.now() + Math.random()
    };
  };

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setLevel(1);
    setCombo(0);
    setHealth(100);
    setTimeLeft(60);
    setEnemies([generateEnemy(), generateEnemy(), generateEnemy()]);
  };

  useEffect(() => {
    if(gameState === 'playing' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(t => {
          if(t <= 1) {
            setGameState('gameover');
            return 0;
          }
          return t - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [gameState, timeLeft]);

  const attackEnemy = (enemy, userAnswer) => {
    if(parseInt(userAnswer) === enemy.answer) {
      setScore(s => s + (50 + combo * 10));
      setCombo(c => c + 1);
      setTimeLeft(t => t + 3);
      setEnemies(prev => {
        const filtered = prev.filter(e => e.id !== enemy.id);
        if(filtered.length < 3) {
          filtered.push(generateEnemy());
        }
        return filtered;
      });
      
      if(score > 0 && score % 500 === 0) {
        setLevel(l => l + 1);
      }
    } else {
      setCombo(0);
      setHealth(h => {
        const newHealth = h - 10;
        if(newHealth <= 0) setGameState('gameover');
        return Math.max(0, newHealth);
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-900 to-black p-8">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-5xl font-bold text-center mb-8 text-yellow-300">
          🥷 Math Ninja Warrior ⚔️
        </h1>

        {gameState === 'menu' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">🥷</div>
            <h2 className="text-3xl font-bold mb-4">Become a Math Ninja!</h2>
            <p className="text-xl mb-8 text-gray-600">
              Defeat enemies by solving math problems quickly!
            </p>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-red-500 to-orange-600 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              START BATTLE
            </button>
          </div>
        )}

        {gameState === 'playing' && (
          <div>
            <div className="bg-white rounded-lg p-4 mb-4 flex justify-between">
              <div className="font-bold">Score: {score}</div>
              <div className="font-bold">Level: {level}</div>
              <div className="font-bold text-orange-600">Combo: {combo}x</div>
              <div className={`font-bold ${health < 30 ? 'text-red-600 animate-pulse' : 'text-green-600'}`}>
                Health: {health}%
              </div>
              <div className="font-bold text-blue-600">Time: {timeLeft}s</div>
            </div>

            <div className="relative bg-gradient-to-b from-gray-800 to-gray-900 rounded-lg p-8" style={{ height: '500px' }}>
              {enemies.map(enemy => (
                <div
                  key={enemy.id}
                  className="absolute bg-red-600 rounded-lg p-6 shadow-2xl transform hover:scale-105 transition"
                  style={{
                    left: `${enemy.x}%`,
                    top: `${enemy.y}%`
                  }}
                >
                  <div className="text-4xl mb-2 text-center">👹</div>
                  <div className="text-white font-bold text-2xl mb-3 text-center">
                    {enemy.question} = ?
                  </div>
                  <input
                    type="number"
                    className="w-full p-2 rounded text-center text-xl font-bold"
                    placeholder="Answer"
                    onKeyPress={(e) => {
                      if(e.key === 'Enter') {
                        attackEnemy(enemy, e.target.value);
                        e.target.value = '';
                      }
                    }}
                  />
                </div>
              ))}

              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
                <div className="text-6xl">🥷</div>
                <div className="text-white font-bold text-center mt-2">YOU</div>
              </div>
            </div>

            {combo > 2 && (
              <div className="text-center mt-4 text-yellow-300 text-3xl font-bold animate-pulse">
                🔥 {combo}x COMBO! 🔥
              </div>
            )}
          </div>
        )}

        {gameState === 'gameover' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">⚔️</div>
            <h2 className="text-4xl font-bold mb-4">Battle Complete!</h2>
            <div className="text-3xl mb-4 text-red-600 font-bold">Final Score: {score}</div>
            <div className="text-2xl mb-8 text-gray-600">
              Max Combo: {combo}x • Level: {level}
            </div>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-red-500 to-orange-600 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              BATTLE AGAIN
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MathNinjaWarrior;
